import { useCallback, useState } from 'react';
import { useGameStore } from '../../store/gameSlice';
import { useMenuNavigation } from '../../hooks/menu/useMenuNavigation';
import { closeInterface, openInterface } from '../../services/InterfaceService';
import { runExitService } from '../../services/RunExitService';
import { useNetSessionStore } from '../../store/netSessionSlice';
import { NineSliceFrame } from './NineSliceFrame';
import { KeyDisplay } from './KeyDisplay';
import { ConfirmPopup } from './ConfirmPopup';
import './EscapeMenu.scss';

// The Quit action always routes to MainMenu, never to the Hub. The Abandon-run
// action is dungeon-only.
export const EscapeMenu = () => {
  const gameplay = useGameStore((s) => s.gameplay);

  const inDungeon = gameplay === 'dungeon';

  const resume = useCallback(() => closeInterface(), []);
  const openInventory = useCallback(() => openInterface('inventory'), []);
  const openOptions = useCallback(() => openInterface('options'), []);
  // Equipment page (read-only loadout + socket boosts) — always reachable from
  // pause so the player can review their setup/stats anytime. Full loadout
  // editing stays hub-only (loadout NPC).
  const openLoadout = useCallback(() => openInterface('equipment'), []);
  const [confirm, setConfirm] = useState<null | 'abandon' | 'quit'>(null);
  // Reactive: the role can flip (e.g. host drop tears the session down) while the
  // menu is open — a one-shot read of netService.role would go stale.
  const role = useNetSessionStore((s) => s.role);
  const isCoop = role !== 'offline';

  const doAbandon = () => {
    setConfirm(null);
    runExitService.abandonRun();
  };
  const doQuit = () => {
    setConfirm(null);
    runExitService.quitGame();
  };
  const onAbandonPressed = () => setConfirm('abandon');
  const onQuitPressed = () => {
    if (isCoop) {
      setConfirm('quit');
    } else {
      runExitService.quitGame();
    }
  };

  const { containerRef } = useMenuNavigation({
    disabled: confirm !== null,
    onConfirm: (id) => {
      switch (id) {
        case 'inventory':
          return openInventory();
        case 'loadout':
          return openLoadout();
        case 'options':
          return openOptions();
        case 'abandon':
          return onAbandonPressed();
        case 'quit':
          return onQuitPressed();
      }
    },
    onCancel: resume,
    onPause: resume,
  });

  // Dynamic row index: the abandon entry is dungeon-only; quit is always present.
  let row = 0;

  return (
    <>
      <div className="escape-overlay">
        <div className="escape-root">
          <NineSliceFrame className="escape-frame">
            <div className="escape-menu" ref={containerRef}>
              <h1 className="escape-title">Pause</h1>
              <div className="escape-buttons">
                <div
                  data-nav-row={row++}
                  data-nav-id="inventory"
                  className="escape-btn"
                >
                  Inventaire
                </div>
                <div
                  data-nav-row={row++}
                  data-nav-id="loadout"
                  className="escape-btn"
                >
                  Équipement
                </div>
                <div
                  data-nav-row={row++}
                  data-nav-id="options"
                  className="escape-btn"
                >
                  Options
                </div>
                {inDungeon && (
                  <div
                    data-nav-row={row++}
                    data-nav-id="abandon"
                    className="escape-btn escape-btn--danger"
                  >
                    Abandonner la run
                  </div>
                )}
                <div
                  data-nav-row={row++}
                  data-nav-id="quit"
                  className="escape-btn"
                >
                  Quitter le jeu
                </div>
              </div>
              <div className="escape-hint">
                <KeyDisplay context="interface" action="CONFIRM" size="sm" />{' '}
                valider ·{' '}
                <KeyDisplay context="interface" action="CANCEL" size="sm" />{' '}
                reprendre
              </div>
            </div>
          </NineSliceFrame>
        </div>
      </div>
      {confirm === 'abandon' && (
        <ConfirmPopup
          title="Abandonner la run"
          message="Abandonner la run en cours ? La progression de cette run sera perdue."
          confirmLabel="Abandonner"
          cancelLabel="Annuler"
          onConfirm={doAbandon}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm === 'quit' && (
        <ConfirmPopup
          title="Quitter le jeu"
          message="Quitter la partie en cours ? La run sera perdue."
          confirmLabel="Quitter"
          cancelLabel="Annuler"
          onConfirm={doQuit}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
};
